import { produce } from 'immer'
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import TextArea from '~components/ui/text-input/text-area'
import TextField from '~components/ui/text-input/text-field'
import getRootPath from '~utils/app/get-root-path'
import Mixpanel from '~utils/mixpanel'
import {
  getIntakeSpecV1,
  getIntakeSpecV2,
  IntakeQuestion,
} from '~repositories/hooks/use-intake-spec'
import usePetLatestWeight from '~repositories/hooks/use-pet-latest-weight'

import { useNavigate } from '@tanstack/react-router'

import ConsultationLayout from './components/layout'
import IntakeAlert from './components/intake-alert'
import { useConsultationIntakeSpec } from './hooks/use-consultation-intake-spec'
import {
  getAllSafetyQuestions,
  getCommonPages,
  getConsultationScreens,
  getAlertHitV2,
  getModuleKey,
  getSafetyQuestions,
  getSafetyScreenNumber,
  getVisibleQuestionsV2,
  pruneHiddenAnswersV2,
  pruneSkippedAnswers,
  SafetyAnswers,
} from './screens'
import CheckSelect from './components/ui/check-select'
import RadioSelect from './components/ui/radio-select'
import {
  alertRank,
  convertWeight,
  INTAKE_UNKNOWN,
  isUnknownAnswer,
  numberPrefillSource,
  showsIntakeAlerts,
  type IntakeAlertLevel,
  type IntakeAnswer,
  type IntakeQuestionV2,
} from './intake-v2'
import { useConsultationData } from './store'
import type { ConsultationData } from './types'

// v1: 다중 선택에서 다른 답과 함께 고를 수 없는 값
const EXCLUSIVE_V1 = ['none', INTAKE_UNKNOWN]

/** 화면 하나에 그리는 질문 (v1 안전 질문과 v2 질문을 같은 모양으로) */
type StepQuestion = Readonly<{
  id: string
  question: string
  type: IntakeQuestionV2['type']
  // '모름'(value 'unknown')과 emptyMeans 값의 보기는 목록에 그리지 않는다 (아래 버튼·빈 답이 맡는다)
  options: Readonly<{ value: string; label: string }>[]
  exclusive: string[]
  // 같은 묶음 안에서는 하나만 고른다 (v2 exclusive_groups, 예: 통증 정도)
  exclusiveGroups: string[][]
  // 답(또는 "I don't know")이 있어야 Next 를 누를 수 있다 (v2 required)
  required: boolean
  hasUnknown: boolean
  // 다중 선택: 아무것도 고르지 않고 Next 를 누르면 이 값으로 답한다 ('none')
  emptyMeans: string | null
  emptyHint?: string
  // "I don't know" 버튼 문구 (질문지의 unknown_label)
  unknownLabel?: string
  v2?: IntakeQuestionV2
}>

const fromV1 = (question: IntakeQuestion): StepQuestion => ({
  id: question.id,
  question: question.question,
  type: question.multiple ? 'multi' : 'single',
  options: question.options,
  exclusive: EXCLUSIVE_V1,
  exclusiveGroups: [],
  required: false,
  hasUnknown: question.options.some(option => option.value === INTAKE_UNKNOWN),
  emptyMeans: null,
})

// title: question_variants 로 고른 제목, hidden: 보기의 show_if 로 숨긴 값 (resolveIntake 결과)
const fromV2 = (
  question: IntakeQuestionV2,
  title: string | undefined,
  hidden: string[] = []
): StepQuestion => ({
  id: question.id,
  question: title ?? question.question,
  type: question.type,
  options: question.options.filter(option => !hidden.includes(option.value)),
  // '모름'과 '해당 없음'(empty_means)은 다른 답과 같이 고를 수 없다
  exclusive: [
    ...question.exclusive,
    INTAKE_UNKNOWN,
    ...(question.empty_means ? [question.empty_means] : []),
  ],
  exclusiveGroups: question.exclusive_groups ?? [],
  required: !!question.required,
  hasUnknown: question.unknown,
  emptyMeans: question.empty_means ?? null,
  emptyHint: question.empty_hint,
  unknownLabel: question.unknown_label,
  v2: question,
})

/** '모름'으로 답했는지. 텍스트는 버튼으로 고른 '모름'만 '모름' (직접 쓴 "unknown" 은 입력한 글) */
const isAnsweredUnknown = (
  question: StepQuestion,
  answer: IntakeAnswer | undefined,
  textUnknownIds: string[]
) => isUnknownAnswer(answer) && (question.type !== 'text' || textUnknownIds.includes(question.id))

/** 프로필 값 미리 채우기를 이 질문에서 끝낸 것으로 표시 (채웠든, 보호자가 먼저 답했든). 다시 채우지 않는다 */
const markPrefillHandled = (draft: ConsultationData, question: StepQuestion) => {
  if (!question.v2 || !numberPrefillSource(question.v2)) return
  if (draft.intake_prefilled_ids.includes(question.id)) return
  draft.intake_prefilled_ids = [...draft.intake_prefilled_ids, question.id]
}

// 답을 저장하고 곧바로 다음 화면으로 넘어가는 중일 때, 누르기 직전 화면 (한 프레임 깜빡임 방지)
type LeavingSnapshot = Readonly<{ answer: IntakeAnswer | undefined; unknown: boolean }>

type ShownAlert = Readonly<{
  level: IntakeAlertLevel
  // '모름'으로 넘어가다 안내가 뜬 경우: 닫으면 다음 질문으로 간다
  nextAnswers?: SafetyAnswers
}>

/**
 * 숫자 질문 (v2 number): 숫자와 단위. prefill_from('pet.weight') 이면 Safety 가 프로필 몸무게를 답으로 넣어 주고,
 * 입력이 비어 있을 때만 그 값을 보여 준다(입력 중인 글은 덮어쓰지 않는다).
 */
const NumberAnswer = ({
  question,
  answer,
  onChange,
}: {
  question: IntakeQuestionV2
  answer: IntakeAnswer | undefined
  onChange: (value: number | null, valid: boolean) => void
}) => {
  const [raw, setRaw] = useState(typeof answer === 'number' ? String(answer) : '')
  const min = question.min ?? 0
  const max = question.max ?? Number.MAX_SAFE_INTEGER
  const parsed = raw === '' ? null : Number(raw)
  // 비어 있지 않은데 범위 밖이거나 숫자가 아니면 ("." 만 입력 등) 입력란 아래에 오류를 보인다
  const invalid = parsed !== null && (!Number.isFinite(parsed) || parsed < min || parsed > max)
  // '0', '0.', '.0' 처럼 작은 수를 입력하는 중이면 Next 만 막고 오류 문구는 아직 띄우지 않는다
  const showError = invalid && !/^0*\.?0*$/.test(raw.trim())

  // 밖에서 채워진 답(프로필 몸무게 미리 채우기)을 빈 입력에 반영한다
  useEffect(() => {
    if (typeof answer === 'number')
      setRaw(current => (current.trim() === '' ? String(answer) : current))
  }, [answer])

  return (
    <div className="flex items-start flex-gap-x-2 w-full">
      <TextField
        className="flex-1"
        type="text"
        inputMode="decimal"
        value={raw}
        error={showError}
        showHelper={showError}
        helperMessage={
          showError
            ? `Enter a number between ${min} and ${max}${question.unit ? ` ${question.unit}` : ''}.`
            : undefined
        }
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          const next = event.target.value
          // 숫자와 소수점 하나만 받는다 (음수·글자·공백은 입력되지 않는다)
          if (!/^\d*\.?\d*$/.test(next)) return
          setRaw(next)
          const value = next === '' ? null : Number(next)
          const valid = value === null || (Number.isFinite(value) && value >= min && value <= max)
          onChange(valid ? value : null, valid)
        }}
      />
      {question.unit && (
        <span className="py-4 text-body-1 text-gray-700 dark:text-gray-500">{question.unit}</span>
      )}
    </div>
  )
}

// 필수 문진: 한 화면에 한 질문. v2 의 required 질문은 답이나 "I don't know" 가 있어야 넘어가고, 나머지는 건너뛸 수 있다.
// v2 (DRT-7186): 질문지 flows[증상]을 show_if 로 거른 목록. 답이 바뀔 때마다 목록을 다시 계산하고
//   숨겨진 질문의 답은 지운다. 답을 고르면 alerts 규칙을 평가하고, 질문지의 show_alerts 가 꺼져 있지 않으면
//   응급 안내를 띄운다. "I don't know" 는 보기 줄을 만들지 않고 바로 다음으로 가며, 다시 들어오면 그 답을 비워 처음 상태로 보인다.
// v1 (폴백, 다음 릴리스에 삭제): 증상별 질문 모듈 → 공통 안전 질문, skip_if 로 건너뜀 (DRT-7112).
const Safety = ({ main_concern, step = 0 }: { main_concern: string; step?: number }) => {
  const navigate = useNavigate()
  // 증상 화면에서 정한 문진 버전의 질문지
  const { data, isPending, isError } = useConsultationIntakeSpec()
  const specV2 = getIntakeSpecV2(data)
  const spec = getIntakeSpecV1(data)
  const [consultationData, setConsultationData] = useConsultationData()
  const [shownAlert, setShownAlert] = useState<ShownAlert | null>(null)
  const [numberInvalid, setNumberInvalid] = useState(false)
  // 저장 → navigate 사이에 새 답으로 한 번 그려지면 "I don't know" 가 고른 모양으로 깜빡이고 입력이 비워진다.
  // 넘어가는 중에는 누르기 직전 모습을 그대로 그린다 (화면은 스텝마다 다시 마운트되므로 되돌릴 필요 없음)
  const [leaving, setLeaving] = useState<LeavingSnapshot | null>(null)
  const answers = useMemo<SafetyAnswers>(
    () =>
      ((consultationData.checkup?.[main_concern] as Record<string, unknown> | null)
        ?.safety as SafetyAnswers) || {},
    [consultationData.checkup, main_concern]
  )
  const pet = consultationData.pet

  // 신규 증상(others.category)이면 그 분류의 모듈 / 흐름 (DRT-7113)
  const category = (consultationData.checkup?.others as Record<string, unknown> | null)
    ?.category as string | undefined
  const moduleKey = getModuleKey(main_concern, category)
  const allQuestions = useMemo(() => getAllSafetyQuestions(spec, moduleKey), [moduleKey, spec])

  // 현재 답 기준으로 실제 묻는 질문 목록 (v2: show_if 로 보이는 질문, v1: skip_if 에 안 걸린 질문)
  const getQuestions = useCallback(
    (current: SafetyAnswers): StepQuestion[] => {
      if (!specV2) return getSafetyQuestions(allQuestions, current).map(fromV1)
      const { visible, questionText, hiddenOptions } = getVisibleQuestionsV2(
        specV2,
        moduleKey,
        current,
        pet
      )
      return visible.map(q => fromV2(q, questionText[q.id], hiddenOptions[q.id]))
    },
    [allQuestions, moduleKey, pet, specV2]
  )
  // 묻지 않게 된 질문의 남은 답을 지운다 (current 를 직접 수정)
  const pruneAnswers = useCallback(
    (current: SafetyAnswers) => {
      if (specV2) pruneHiddenAnswersV2(specV2, moduleKey, current, pet)
      else pruneSkippedAnswers(allQuestions, current)
    },
    [allQuestions, moduleKey, pet, specV2]
  )

  // step 은 이 목록의 인덱스라 뒤로/앞으로 가도 일관된다
  const questions = useMemo(() => getQuestions(answers), [answers, getQuestions])
  // 범위를 벗어난 step(주소 직접 입력, 질문이 줄어든 뒤의 앞으로 가기)은 마지막 질문으로 맞춘다
  const currentStep = Math.min(step, questions.length - 1)
  const question: StepQuestion | undefined = questions[currentStep]
  const screens = useMemo(
    () => getConsultationScreens({ concern: main_concern, category, spec: data, answers, pet }),
    [answers, category, data, main_concern, pet]
  )

  /** 답을 바꾸고(빈 답은 키 생략, 숨겨진 질문의 답 삭제) 바뀐 답을 돌려준다 */
  const commitAnswers = useCallback(
    (update: (safety: SafetyAnswers) => void, extra?: (draft: ConsultationData) => void) => {
      const next: SafetyAnswers = { ...answers }
      update(next)
      // 비어 있는 다중 선택·텍스트는 건너뜀(키 생략)
      for (const key of Object.keys(next)) {
        const answer = next[key]
        if ((Array.isArray(answer) && answer.length === 0) || answer === '') delete next[key]
      }
      pruneAnswers(next)
      setConsultationData(
        produce(draft => {
          const target = draft.checkup?.[main_concern] as Record<string, unknown> | null | undefined
          if (target) target.safety = next
          extra?.(draft)
        })
      )
      return next
    },
    [answers, main_concern, pruneAnswers, setConsultationData]
  )

  // 질문 화면에 들어올 때(뒤로 와서 다시 볼 때 포함) "I don't know" 로 한 답은 비우고 처음 상태로 보여 준다 (QA round 2).
  // 버튼은 누르면 바로 넘어가는 동작이라 선택된 모양으로 남겨 두지 않는다. 고른 선택지·입력은 그대로 둔다.
  // 응급 안내로 같은 화면에 머무를 때는 질문이 바뀌지 않으므로 비우지 않는다
  const [arrivedAt, setArrivedAt] = useState<string | null>(null)
  useEffect(() => {
    if (!question || arrivedAt === question.id) return
    setArrivedAt(question.id)
    if (
      !isAnsweredUnknown(question, answers[question.id], consultationData.intake_text_unknown_ids)
    ) {
      return
    }
    // 그 키만 지운다(commitAnswers 의 정리를 거치지 않음): '모름'에 걸려 보이던 뒤 질문의 답을 지나가기만 해도 잃지 않게.
    // 숨겨진 답이 남아도 제출 때 서버(resolve)가 보이는 질문의 답만 남긴다
    const id = question.id
    setConsultationData(
      produce((draft: ConsultationData) => {
        const target = draft.checkup?.[main_concern] as Record<string, unknown> | null | undefined
        const safety = target?.safety as SafetyAnswers | undefined
        if (target && safety) {
          const rest = { ...safety }
          delete rest[id]
          target.safety = rest
        }
        draft.intake_text_unknown_ids = draft.intake_text_unknown_ids.filter(v => v !== id)
      })
    )
  }, [
    answers,
    arrivedAt,
    consultationData.intake_text_unknown_ids,
    main_concern,
    question,
    setConsultationData,
  ])

  /**
   * 응급 안내 (v2, DESIGN 8절): 답이 바뀔 때마다 흐름 전체의 alerts 규칙을 보이는 질문의 답으로 평가해
   * 가장 높은 단계가 이 상담에서 이미 띄운 단계보다 높을 때만 띄운다(앞 답을 고쳐 뒤 질문의 규칙이 참이 돼도 띄움).
   * 질문지의 show_alerts 가 false 면(지금) 안내·Mixpanel·intake_alert_shown 모두 없이 문진을 끝까지 진행한다.
   * 띄웠으면 true. advance: 닫으면 다음 질문으로 간다('모름'으로 넘어가던 중)
   */
  const checkAlert = useCallback(
    (nextAnswers: SafetyAnswers, advance: boolean) => {
      if (!specV2 || !showsIntakeAlerts(specV2)) return false
      const hit = getAlertHitV2(specV2, moduleKey, nextAnswers, pet)
      if (!hit || alertRank(hit.level) <= alertRank(consultationData.intake_alert_level)) {
        return false
      }
      setConsultationData(
        produce(draft => {
          draft.intake_alert_level = hit.level
        })
      )
      setShownAlert({ level: hit.level, nextAnswers: advance ? nextAnswers : undefined })
      Mixpanel.logEvent('intake_alert_shown', {
        level: hit.level,
        question_id: hit.questionId,
        option: hit.option,
      })
      return true
    },
    [consultationData.intake_alert_level, moduleKey, pet, setConsultationData, specV2]
  )

  const setChoice = useCallback(
    (target: StepQuestion, value: string) => {
      const next = commitAnswers(safety => {
        if (target.type !== 'multi') {
          // 같은 답을 다시 누르면 해제(건너뜀)
          if (safety[target.id] === value) delete safety[target.id]
          else safety[target.id] = value
          return
        }
        const current = Array.isArray(safety[target.id]) ? (safety[target.id] as string[]) : []
        // 같은 exclusive_groups 묶음의 다른 값은 해제한다 (예: 통증 정도는 하나만)
        const group = target.exclusiveGroups.find(g => g.includes(value)) ?? []
        if (current.includes(value)) safety[target.id] = current.filter(v => v !== value)
        else if (target.exclusive.includes(value)) safety[target.id] = [value]
        else
          safety[target.id] = [
            ...current.filter(v => !target.exclusive.includes(v) && !group.includes(v)),
            value,
          ]
      })
      checkAlert(next, false)
    },
    [checkAlert, commitAnswers]
  )

  const setFreeAnswer = useCallback(
    (target: StepQuestion, value: string | number | null) => {
      const next = commitAnswers(
        safety => {
          // 공백만 있는 텍스트는 제출할 때 뺀다(입력 중에는 그대로 둔다)
          if (value === null || value === '') delete safety[target.id]
          else safety[target.id] = value
        },
        // 직접 입력한 글은 "unknown" 이라고 써도 입력한 그대로 보여 준다(버튼으로 고른 '모름'만 '모름')
        draft => {
          draft.intake_text_unknown_ids = draft.intake_text_unknown_ids.filter(
            id => id !== target.id
          )
          // 보호자가 직접 고친 질문은 늦게 온 프로필 값으로 채우지 않는다
          markPrefillHandled(draft, target)
        }
      )
      checkAlert(next, false)
    },
    [checkAlert, commitAnswers]
  )

  // 질문을 다 보면: v2 는 상세 입력/제출, v1 은 공통 이력(period → vaccine → prevention)
  const finish = useCallback(
    (replace = false) => {
      navigate({
        to: specV2
          ? `${getRootPath()}/ask-a-vet/attachment`
          : `${getRootPath()}/ask-a-vet/preliminary-checkup/${main_concern}/${getCommonPages()[0]}/`,
        replace,
        resetScroll: true,
      })
    },
    [main_concern, navigate, specV2]
  )

  // nextAnswers: 이 화면에서 답이 바뀌면서 곧바로 넘어가는 경우, 바뀐 답 기준으로 다음 질문을 정한다
  const onNextPress = useCallback(
    (nextAnswers: SafetyAnswers = answers) => {
      if (currentStep + 1 < getQuestions(nextAnswers).length) {
        navigate({
          to: `${getRootPath()}/ask-a-vet/safety/${main_concern}`,
          // to 가 동적 경로(getRootPath)라 search 타입이 모든 라우트의 합집합으로 잡혀서 단언한다
          search: { step: currentStep + 1 } as never,
          resetScroll: true,
        })
        return
      }
      finish()
    },
    [answers, currentStep, finish, getQuestions, main_concern, navigate]
  )

  // Next: 다중 선택에 empty_means 가 있고 아무것도 고르지 않았으면 그 값('none')으로 답하고 넘어간다
  const onNextButton = useCallback(() => {
    const emptyMeans = question?.emptyMeans
    if (
      !question ||
      question.type !== 'multi' ||
      !emptyMeans ||
      answers[question.id] !== undefined
    ) {
      return onNextPress()
    }
    setLeaving({ answer: answers[question.id], unknown: false })
    const next = commitAnswers(safety => {
      safety[question.id] = [emptyMeans]
    })
    if (checkAlert(next, true)) return
    onNextPress(next)
  }, [answers, checkAlert, commitAnswers, onNextPress, question])

  // "I don't know": '모름' 답이 있으면 그걸 저장하고(v2 텍스트·숫자도 'unknown') 바로 넘어간다. 없으면 답을 비우고 넘어간다
  // 이미 '모름'이면 그대로 둔 채 넘어간다
  const onSkipPress = useCallback(() => {
    if (!question) return onNextPress()
    const current = answers[question.id]
    setLeaving({
      answer: current,
      unknown: isAnsweredUnknown(question, current, consultationData.intake_text_unknown_ids),
    })
    const next = commitAnswers(
      safety => {
        if (!question.hasUnknown) delete safety[question.id]
        else safety[question.id] = question.type === 'multi' ? [INTAKE_UNKNOWN] : INTAKE_UNKNOWN
      },
      draft => {
        if (question.type === 'text' && question.hasUnknown) {
          draft.intake_text_unknown_ids = [
            ...draft.intake_text_unknown_ids.filter(id => id !== question.id),
            question.id,
          ]
        }
        markPrefillHandled(draft, question)
      }
    )
    if (checkAlert(next, true)) return
    onNextPress(next)
  }, [
    answers,
    checkAlert,
    commitAnswers,
    consultationData.intake_text_unknown_ids,
    onNextPress,
    question,
  ])

  const closeAlert = useCallback(
    (action: 'find_clinic' | 'continue') => {
      if (!shownAlert) return
      Mixpanel.logEvent('intake_alert_action', { action, level: shownAlert.level })
      // SmartThings 에는 병원 찾기 화면이 없어 "Find a clinic now" 도 안내를 닫는다(문구만 유지)
      setShownAlert(null)
      if (shownAlert.nextAnswers) onNextPress(shownAlert.nextAnswers)
    },
    [onNextPress, shownAlert]
  )

  // 질문을 보여 줬다는 표시: 전부 건너뛰어도 빈 객체로 보낸다(서버가 '건너뜀'과 '안 물음'을 구분)
  useEffect(() => {
    if (!question) return
    setConsultationData(
      produce(draft => {
        const target = draft.checkup?.[main_concern] as Record<string, unknown> | null | undefined
        if (target && !target.safety) target.safety = {}
      })
    )
  }, [main_concern, question, setConsultationData])

  // 몸무게 미리 채우기 (v2 number, prefill_from 'pet.weight'): 프로필의 최신 몸무게를 질문 단위로 바꿔 답으로 넣는다.
  // 한 상담에서 질문마다 한 번만 채운다(intake_prefilled_ids: 지운 값을 뒤로 갔을 때 다시 채우지 않게. 화면은 스텝마다
  // 다시 마운트된다). 응답이 오기 전에 보호자가 이미 답했으면('모름' 포함) 값은 쓰지 않고 처리한 것으로만 표시한다.
  // SmartThings 펫 프로필에는 몸무게가 없어 보통 기록이 없다. 제출 후 프로필에 되쓰기(save_to)는 하지 않는다.
  const prefillSource = question?.v2 ? numberPrefillSource(question.v2) : null
  const prefilled = !!question && consultationData.intake_prefilled_ids.includes(question.id)
  const { data: latestWeight } = usePetLatestWeight(
    pet?.id,
    prefillSource === 'pet.weight' && !prefilled
  )
  useEffect(() => {
    if (!question?.v2 || prefillSource !== 'pet.weight' || prefilled || !latestWeight) return
    const value = convertWeight(latestWeight.value, latestWeight.unit, question.v2.unit)
    const min = question.v2.min ?? 0
    const max = question.v2.max ?? Number.MAX_SAFE_INTEGER
    const usable =
      answers[question.id] === undefined && value !== null && value >= min && value <= max
    commitAnswers(
      safety => {
        if (usable) safety[question.id] = value
      },
      draft => markPrefillHandled(draft, question)
    )
  }, [answers, commitAnswers, latestWeight, prefillSource, prefilled, question])

  // 질문지를 받지 못했거나(구 서버·오류) 질문이 없으면 빈 화면 대신 다음 단계로 넘긴다
  // (뒤로 가기에서 되돌아오지 않게 현재 기록을 대체)
  useEffect(() => {
    // isPending: 오프라인 등으로 질문지를 아직 못 받았으면 기다린다(문진을 조용히 건너뛰지 않게)
    if (!isPending && (isError || !question)) finish(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, isPending, question])

  if (!question) return null

  // 넘어가는 중이면 누르기 직전 답으로 그린다 (leaving)
  const storedAnswer = leaving ? leaving.answer : answers[question.id]
  // '모름'은 아래 I don't know 버튼이 맡는다: 보기 줄로 그리지 않는다. 다시 들어오면 위 effect 가 비우므로
  // 버튼이 고른 상태로 보이는 건 응급 안내로 이 화면에 머무를 때뿐이다
  const storedUnknown = leaving
    ? leaving.unknown
    : isAnsweredUnknown(question, storedAnswer, consultationData.intake_text_unknown_ids)
  // 들어오는 첫 렌더(effect 가 비우기 전)도 처음 상태로 그린다: 고른 버튼이 한 프레임 보이지 않게
  const arriving = !leaving && arrivedAt !== question.id
  const answer = arriving && storedUnknown ? undefined : storedAnswer
  const answeredUnknown = storedUnknown && !arriving
  // empty_means 값('none')의 보기도 숨긴다: 아무것도 안 고르고 Next = 그 답. 뒤로 오면 아무것도 안 고른 모습
  // 공백만 있는 글은 제출할 때 빠지므로 답이 아니다
  const hasAnswer = answer !== undefined && !(typeof answer === 'string' && answer.trim() === '')
  const visibleOptions = question.options.filter(
    option => option.value !== INTAKE_UNKNOWN && option.value !== question.emptyMeans
  )
  return (
    <>
      <ConsultationLayout
        key={question.id}
        title={question.question}
        // 다중 선택 질문은 기존 증상 화면처럼 여러 개 고를 수 있다고 알려 준다
        description={question.type === 'multi' ? 'Select all that apply.' : undefined}
        hint={question.type === 'multi' ? question.emptyHint : undefined}
        // 필수 질문(required)은 답이나 "I don't know" 가 있어야 넘어간다. 숫자는 범위 안의 값이어야 한다
        nextButtonDisabled={
          (question.type === 'number' && numberInvalid) || (question.required && !hasAnswer)
        }
        onNextPress={onNextButton}
        onSkipPress={onSkipPress}
        skipLabel={question.unknownLabel}
        skipSelected={answeredUnknown}
        progress={{ current: getSafetyScreenNumber(screens, currentStep), total: screens.total }}
      >
        <div className="flex flex-col flex-gap-y-2 w-full">
          {question.type === 'text' && question.v2 && (
            <TextArea
              type="box"
              className="h-36 w-full"
              value={answeredUnknown || typeof answer !== 'string' ? '' : answer}
              placeholder={question.v2.placeholder}
              maxLength={question.v2.max_length}
              onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                setFreeAnswer(question, event.target.value)
              }
            />
          )}
          {question.type === 'number' && question.v2 && (
            <NumberAnswer
              question={question.v2}
              answer={answer}
              onChange={(value, valid) => {
                setNumberInvalid(!valid)
                setFreeAnswer(question, value)
              }}
            />
          )}
          {visibleOptions.map(option => {
            const item = { value: option.value, label: option.label }
            const isSelected = Array.isArray(answer)
              ? answer.includes(option.value)
              : answer === option.value
            return question.type === 'multi' ? (
              <CheckSelect
                key={option.value}
                root={answers}
                item={item}
                isSelected={isSelected}
                onSelect={() => setChoice(question, option.value)}
              />
            ) : (
              <RadioSelect
                key={option.value}
                root={answers}
                item={item}
                isSelected={isSelected}
                onSelect={() => setChoice(question, option.value)}
              />
            )
          })}
        </div>
      </ConsultationLayout>
      {shownAlert && specV2 && (
        <IntakeAlert
          level={shownAlert.level}
          copy={specV2.alerts}
          onFindClinic={() => closeAlert('find_clinic')}
          onContinue={() => closeAlert('continue')}
          // 버튼을 누르지 않고 사라짐(뒤로 가기 등)
          onDismiss={() =>
            Mixpanel.logEvent('intake_alert_action', {
              action: 'dismissed',
              level: shownAlert.level,
            })
          }
        />
      )}
    </>
  )
}

export default Safety
